import './floorplan-floor';
import '../input/button-input';

import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { cache } from 'lit/directives/cache.js';

type Point = [number, number];

export interface Annotation {
    text: string;
    position: Point;
    offset: Point;
    rotation: number;
}

export interface Icon {
    name: string;
    position: Point;
}

export interface Floor {
    label: string;
    name: string;
    icons: Icon[];
    annotations: Annotation[];
}

@customElement('floorplan-element')
export class FloorplanElement extends LitElement {

    // Props

    @property({ type: Number })
    activeIndex: number = 2;

    private _floors: Floor[] = [
        { 
            label: 'Rooftop Terrace', 
            name: 'terrace',
            icons: [
                {
                    name: 'stairs',
                    position: [12,83],
                },
                {
                    name: 'elevator',
                    position: [67,31],
                },
                {
                    name: 'stairs',
                    position: [92,31],
                },
            ],
            annotations: [
                {
                    text: 'St. Marks Place',
                    position: [50,0],
                    offset: [-50,-100],
                    rotation: 0,
                },
                {
                    text: '2nd Avenue',
                    position: [0,50],
                    offset: [-50,-100],
                    rotation: -90,
                },
                {
                    text: 'Terrace',
                    position: [30,50],
                    offset: [-50,-50],
                    rotation: 0,
                },
            ],
        },
        { 
            label: '2nd Floor', 
            name: 'second',
            icons: [
                {
                    name: 'stairs',
                    position: [12,83],
                },
                {
                    name: 'elevator',
                    position: [67,31],
                },
                {
                    name: 'stairs',
                    position: [92,31],
                },
                {
                    name: 'wc',
                    position: [90,75],
                },
            ],
            annotations: [
                {
                    text: 'St. Marks Place',
                    position: [50,0],
                    offset: [-50,-100],
                    rotation: 0,
                },
                {
                    text: '2nd Avenue',
                    position: [0,50],
                    offset: [-50,-100],
                    rotation: -90,
                },
                {
                    text: 'Library',
                    position: [13,40],
                    offset: [-50,-50],
                    rotation: 0,
                },
                {
                    text: 'Gallery',
                    position: [45,50],
                    offset: [-50,-50],
                    rotation: 0,
                },
            ],
        },
        { 
            label: '★ 1st Floor', 
            name: 'ground',
            icons: [
                {
                    name: 'stairs',
                    position: [12,83],
                },
                {
                    name: 'elevator',
                    position: [67,31],
                },
                {
                    name: 'stairs',
                    position: [92,31],
                },
                {
                    name: 'info',
                    position: [12,50],
                },
            ],
            annotations: [
                {
                    text: 'St. Marks Place',
                    position: [50,0],
                    offset: [-50,-100],
                    rotation: 0,
                },
                {
                    text: '2nd Avenue',
                    position: [0,50],
                    offset: [-50,-100],
                    rotation: -90,
                },
                {
                    text: 'Gallery',
                    position: [45,50],
                    offset: [-50,-50],
                    rotation: 0,
                },
            ],
        },
        { 
            label: 'Lower Level', 
            name: 'lower',
            icons: [
                {
                    name: 'stairs',
                    position: [12,83],
                },
                {
                    name: 'elevator',
                    position: [67,31],
                },
                {
                    name: 'stairs',
                    position: [92,31],
                },
                {
                    name: 'wc',
                    position: [40,83],
                },
            ],
            annotations: [
                {
                    text: 'St. Marks Place',
                    position: [50,0],
                    offset: [-50,-100],
                    rotation: 0,
                },
                {
                    text: '2nd Avenue',
                    position: [0,50],
                    offset: [-50,-100],
                    rotation: -90,
                },
                {
                    text: 'Gallery',
                    position: [25,30],
                    offset: [-50,-50],
                    rotation: 0,
                },
            ],
        }
    ];

    // Lifecycle Hooks

    connectedCallback() {
        super.connectedCallback();
        this.classList.add('Floorplan');
    }

    // Render

    render() {
        return html`
            <div class="Floorplan-nav">
                ${ this._floors.map((floor, i) => this.buttonTemplate(floor, i)) }
            </div>
            <div class="Floorplan-display">
                ${ cache(this.floorTemplate(this._floors[this.activeIndex])) }
            </div>
        `;
    }

    createRenderRoot() { 
        return this;
    }

    // Templates

    floorTemplate(floor: Floor): TemplateResult {
        return html`
            <floorplan-floor
                title="${ floor.label }"
                name="${ floor.name }"
                .icons="${ floor.icons }"
                .annotations="${ floor.annotations }">
            </floorplan-floor>
        `;
    }

    buttonTemplate(floor: Floor, i): TemplateResult {
        return html`
            <button-input
                .active="${ i == this.activeIndex }"
                type="action"
                name="floor"
                @click="${ () => this._toggle(i) }">
                <div slot="label">
                    ${ floor.label }
                </div>
            </button-input>
        `;
    }

    // Getters

    private _toggle(i) {
        this.activeIndex = i;
    }

}